import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Eye, EyeOff, Loader2, ArrowRight } from "lucide-react";
import AuthShell from "../components/AuthShell";
import GoogleSignInButton from "../components/GoogleSignInButton";

function apiError(e) {
  const d = e?.response?.data?.detail;
  if (!d) return "Une erreur est survenue. Réessayez.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
  return String(d);
}

export default function Login() {
  const nav = useNavigate();
  const loc = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email.trim(), password);
      const redir = loc.state?.from || "/aujourdhui";
      nav(redir, { replace: true });
    } catch (ex) {
      setErr(apiError(ex));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell dataTestId="login-page" title="Content de vous revoir" subtitle="Connectez-vous à votre espace RentaScan.">
      <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
        <div>
          <label className="block text-[12px] font-semibold text-[#111111] mb-1.5">Adresse e-mail</label>
          <input type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email"
            className="w-full h-11 px-3 rounded-md border border-[#CDD7E3] bg-white text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2878FF]/25 focus:border-[#2878FF]" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-[12px] font-semibold text-[#111111]">Mot de passe</label>
            <Link to="/mot-de-passe-oublie" className="text-[11.5px] text-[#1769E0] hover:text-[#0B1F33] font-medium" data-testid="forgot-password-link">Mot de passe oublié ?</Link>
          </div>
          <div className="relative">
            <input type={showPw ? "text" : "password"} required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
              className="w-full h-11 pl-3 pr-10 rounded-md border border-[#CDD7E3] bg-white text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2878FF]/25 focus:border-[#2878FF]" />
            <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="toggle-pw" className="absolute right-2 top-1/2 -translate-y-1/2 text-[#5E6875] hover:text-[#111111]">
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {err && <div className="text-[12.5px] text-[#7C2A21] bg-[#FDECEC] border border-[#F5C7C7] rounded-md px-3 py-2" data-testid="login-error">{err}</div>}

        <button type="submit" disabled={loading || !email || !password} data-testid="login-submit"
          className="w-full h-11 rounded-md text-[#111111] text-[14px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
          style={{ background: "#FF9F1C" }}
          onMouseOver={(e) => e.currentTarget.style.background = "#F58F06"}
          onMouseOut={(e) => e.currentTarget.style.background = "#FF9F1C"}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          Se connecter
        </button>
      </form>

      <div className="mt-4 flex items-center gap-3">
        <div className="flex-1 h-px bg-[#CDD7E3]" aria-hidden />
        <span className="text-[11px] uppercase tracking-widest font-bold text-[#5E6875]">ou</span>
        <div className="flex-1 h-px bg-[#CDD7E3]" aria-hidden />
      </div>
      <div className="mt-4">
        <GoogleSignInButton />
      </div>

      <div className="mt-6 text-[13px] text-[#5E6875] text-center">
        Pas encore de compte ?{" "}
        <Link to="/inscription" data-testid="signup-link" className="font-semibold text-[#1769E0] hover:text-[#0B1F33]">Créer un compte</Link>
      </div>
    </AuthShell>
  );
}
